// BHP-1053: resolves and loads the app-wide active customer against Backend2.0.
// The active uuid comes from the persisted store (falling back to the first of
// the account's customers once the backend cannot find it, BHP-1259); the full
// customer is fetched via the Hydra item hook.
import { useEffect } from 'react';
import { useItem } from '../../../shared/api/useHydra';
import { ApiError } from '../../../shared/http/ApiError';
import { useCurrentAccount } from '../../account/application/useCurrentAccount';
import { resolveActiveCustomerUuid } from '../domain/activeCustomer';
import type { Customer } from '../domain/customer';
import { useActiveCustomerStore } from './activeCustomerStore';

export interface ActiveCustomerState {
  activeUuid: string | null;
  customer: Customer | undefined;
  isLoading: boolean;
  /** A (re)fetch of the active customer is in flight — e.g. right after a switch. */
  isFetching: boolean;
  hasNoCustomers: boolean;
}

export function useActiveCustomer(): ActiveCustomerState {
  const { data: account, isPending: accountPending } = useCurrentAccount();
  const storedUuid = useActiveCustomerStore(
    (state) => state.activeCustomerUuid
  );
  const setActiveCustomer = useActiveCustomerStore(
    (state) => state.setActiveCustomer
  );

  const customerUuids = account?.customerUuids ?? [];
  const chosenUuid = resolveActiveCustomerUuid(customerUuids, storedUuid);

  const customerQuery = useItem<Customer>(
    'customers',
    chosenUuid ?? '',
    chosenUuid !== null
  );

  // BHP-1259: the backend decides which customers the caller may select (an
  // admin any, a user their own), and answers 404 for any other: a deleted
  // customer, or one the account was unlinked from.
  const storedIsUnreachable =
    chosenUuid === storedUuid &&
    customerQuery.error instanceof ApiError &&
    customerQuery.error.status === 404;
  const activeUuid = storedIsUnreachable
    ? resolveActiveCustomerUuid(customerUuids, storedUuid, true)
    : chosenUuid;

  // Persist the resolved choice so it survives reloads and navigation.
  useEffect(() => {
    if (activeUuid !== null && activeUuid !== storedUuid) {
      setActiveCustomer(activeUuid);
    }
  }, [activeUuid, storedUuid, setActiveCustomer]);

  return {
    activeUuid,
    customer: customerQuery.data,
    isLoading:
      accountPending || (activeUuid !== null && customerQuery.isPending),
    isFetching: customerQuery.isFetching,
    hasNoCustomers: !accountPending && customerUuids.length === 0
  };
}
